import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../../../utils/cn';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'outlined' | 'ghost' | 'interactive';
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  hover?: boolean;
  className?: string;
  children: React.ReactNode;
}

const CARD_VARIANTS = {
  default: 'bg-surface-primary border border-neutral-200',
  elevated: 'bg-surface-elevated shadow-lg border border-neutral-200/50',
  outlined: 'bg-transparent border-2 border-neutral-300',
  ghost: 'bg-surface-secondary/50 border border-neutral-200/30',
  interactive: 'bg-surface-primary border border-neutral-200 hover:border-primary-300 hover:shadow-md cursor-pointer'
};

const CARD_PADDING = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
  xl: 'p-8'
};

const CARD_RADIUS = {
  none: 'rounded-none',
  sm: 'rounded-md',
  md: 'rounded-lg',
  lg: 'rounded-xl',
  xl: 'rounded-2xl',
  '2xl': 'rounded-3xl'
};

export const Card = forwardRef<HTMLDivElement, CardProps>(({
  variant = 'default',
  padding = 'md',
  radius = 'lg',
  hover = false,
  className,
  children,
  ...props
}, ref) => {
  const isInteractive = variant === 'interactive' || hover;

  const CardComponent = isInteractive ? motion.div : 'div';

  return (
    <CardComponent
      ref={ref}
      className={cn(
        // Base styles
        'transition-all duration-200',
        
        // Variant styles
        CARD_VARIANTS[variant],
        
        // Padding styles
        CARD_PADDING[padding],
        
        // Radius styles
        CARD_RADIUS[radius],
        
        // Custom className
        className
      )}
             {...(isInteractive ? {
         whileHover: { y: -2, scale: 1.01 },
         whileTap: { scale: 0.99 },
         transition: { duration: 0.2 }
       } : {})}
       {...(props as any)}
    >
      {children}
    </CardComponent>
  );
});

Card.displayName = 'Card';

// Card Header Component
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(({
  className,
  children,
  ...props
}, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex flex-col space-y-1.5 pb-4',
      className
    )}
    {...props}
  >
    {children}
  </div>
));

CardHeader.displayName = 'CardHeader';

// Card Title Component
export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  className?: string;
  children: React.ReactNode;
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(({
  as: Component = 'h3',
  className,
  children,
  ...props
}, ref) => (
  <Component
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight text-neutral-900',
      className
    )}
    {...props}
  >
    {children}
  </Component>
));

CardTitle.displayName = 'CardTitle';

// Card Description Component
export interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  className?: string;
  children: React.ReactNode;
}

export const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(({
  className,
  children,
  ...props
}, ref) => (
  <p
    ref={ref}
    className={cn(
      'text-sm text-neutral-600 leading-relaxed',
      className
    )}
    {...props}
  >
    {children}
  </p>
));

CardDescription.displayName = 'CardDescription';

// Card Content Component
export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(({
  className,
  children,
  ...props
}, ref) => (
  <div
    ref={ref}
    className={cn(
      'pt-0',
      className
    )}
    {...props}
  >
    {children}
  </div>
));

CardContent.displayName = 'CardContent';

// Card Footer Component
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(({
  className,
  children,
  ...props
}, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-center pt-4',
      className
    )}
    {...props}
  >
    {children}
  </div>
));

CardFooter.displayName = 'CardFooter';

// Stat Card Component - Specialized for metrics
export interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: {
    value: number;
    label: string;
    direction: 'up' | 'down' | 'neutral';
  };
  icon?: React.ReactNode;
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  trend,
  icon,
  className
}) => {
  const getTrendColor = (direction: string) => {
    switch (direction) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-neutral-600';
    }
  };

  return (
    <Card variant="elevated" className={className}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-neutral-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-neutral-900 mb-1">{value}</p>
          {subtitle && (
            <p className="text-sm text-neutral-500">{subtitle}</p>
          )}
          {trend && (
            <div className={cn('text-sm font-medium mt-2', getTrendColor(trend.direction))}>
              {trend.direction === 'up' ? '↗' : trend.direction === 'down' ? '↘' : '→'} {trend.label}
            </div>
          )}
        </div>
        {icon && (
          <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
            {icon}
          </div>
        )}
      </div>
    </Card>
  );
}; 